/* ==========================================================================
   Homellence – Hausrundgang
   Läuft von selbst wie ein Film und hört damit endgültig auf, sobald
   jemand selbst bedient. Stationen weiterschalten per Klick, Wischen
   und Pfeiltasten.
   Klick auf ein Produkt öffnet die Schnellansicht über die
   Section Rendering API. Ohne JavaScript stehen alle Stationen untereinander.
   ========================================================================== */
(function () {
  'use strict';

  var HL = (window.HL = window.HL || {});

  class HlHouseTour extends HTMLElement {
    connectedCallback() {
      this.stations = Array.prototype.slice.call(this.querySelectorAll('[data-hl-station]'));
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-hl-step]'));
      this.prevButton = this.querySelector('[data-hl-tour-prev]');
      this.nextButton = this.querySelector('[data-hl-tour-next]');
      this.nextLabel = this.querySelector('[data-hl-tour-next-label]');
      this.liveRegion = this.querySelector('[data-hl-tour-live]');
      if (this.stations.length < 2) return;

      this.index = 0;
      var self = this;

      this.steps.forEach(function (step, i) {
        step.addEventListener('click', function () {
          self.stopAuto();
          self.goTo(i);
        });
      });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', function () {
          self.stopAuto();
          self.goTo(self.index - 1);
        });
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', function () {
          self.stopAuto();
          if (self.index === self.stations.length - 1) {
            self.finish();
            return;
          }
          self.goTo(self.index + 1);
        });
      }

      // Die Tür an der Fassade ist selbst der Auslöser – nicht jeder
      // klickt auf einen Knopf.
      var doorTrigger = this.querySelector('[data-hl-door-trigger]');
      if (doorTrigger) {
        doorTrigger.addEventListener('click', function () {
          self.stopAuto();
          self.goTo(self.index + 1);
        });
        doorTrigger.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
          event.preventDefault();
          self.stopAuto();
          self.goTo(self.index + 1);
        });
      }

      this.addEventListener('keydown', function (event) {
        if (event.target.closest('input, textarea, select')) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          self.stopAuto();
          self.goTo(self.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          self.stopAuto();
          self.goTo(self.index - 1);
        }
      });

      this.setupSwipe();

      this.addEventListener('click', function (event) {
        var hotspot = event.target.closest('[data-hl-quick-view]');
        if (!hotspot) return;
        event.preventDefault();
        self.stopAuto();
        HL.openQuickView(hotspot.getAttribute('data-hl-quick-view'), hotspot);
      });

      this.render();
      this.setupAuto();
    }

    /* ------------------------------------------------------------------
       Filmlauf: die Fortschrittslinie ist zugleich der Taktgeber. Sie
       läuft über die Web-Animations-API, und deren `finish` schaltet
       weiter – so können Linie und Wechsel nie auseinanderlaufen.
       ------------------------------------------------------------------ */
    setupAuto() {
      var self = this;
      this.progress = this.querySelector('[data-hl-tour-progress]');
      this.pauseButton = this.querySelector('[data-hl-tour-pause]');
      this.autoMs = parseInt(this.getAttribute('data-autoplay-ms'), 10) || 9000;

      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.hasAttribute('data-autoplay') || reduced.matches) return;
      if (!this.progress || typeof this.progress.animate !== 'function') return;

      this.autoActive = true;
      this.inView = false;
      this.classList.add('is-auto');

      if (this.pauseButton) {
        this.pauseButton.hidden = false;
        this.pauseButton.addEventListener('click', function () {
          self.userPaused = !self.userPaused;
          self.pauseButton.setAttribute('aria-pressed', self.userPaused ? 'true' : 'false');
          self.pauseButton.setAttribute(
            'aria-label',
            self.pauseButton.getAttribute(self.userPaused ? 'data-label-play' : 'data-label-pause')
          );
          self.syncDwell();
        });
      }

      // Nur solange der Rundgang wirklich zu sehen ist. Beim Wiedereintritt
      // beginnt die Standzeit von vorn, nicht mittendrin.
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
              if (visible === self.inView) return;
              self.inView = visible;
              if (visible) self.startDwell();
              else self.syncDwell();
            });
          },
          { threshold: [0, 0.5, 1] }
        );
        observer.observe(this);
      } else {
        this.inView = true;
      }

      document.addEventListener('visibilitychange', function () {
        self.syncDwell();
      });

      // Auf dem Rechner hält der Takt an, solange die Maus auf der Bühne
      // steht, und läuft zwei Sekunden nach dem Verlassen weiter.
      if (window.matchMedia('(hover: hover)').matches) {
        var stage = this.querySelector('[data-hl-tour-stage]');
        if (stage) {
          stage.addEventListener('mouseenter', function () {
            clearTimeout(self._hoverTimer);
            self.hovering = true;
            self.syncDwell();
          });
          stage.addEventListener('mouseleave', function () {
            clearTimeout(self._hoverTimer);
            self._hoverTimer = setTimeout(function () {
              self.hovering = false;
              self.syncDwell();
            }, 2000);
          });
        }
      }

      this.startDwell();
    }

    /* Standzeit dieser Station: eigener Wert, sonst der Takt der Section. */
    dwellFor(index) {
      var station = this.stations[index];
      var own = station ? parseInt(station.getAttribute('data-dwell'), 10) : 0;
      return own > 0 ? own : this.autoMs;
    }

    startDwell() {
      if (!this.autoActive || !this.progress) return;
      var self = this;
      if (this.dwell) this.dwell.cancel();
      this.dwell = this.progress.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: this.dwellFor(this.index), easing: 'linear', fill: 'forwards' }
      );
      this.dwell.onfinish = function () {
        self.advance();
      };
      this.syncDwell();
    }

    syncDwell() {
      if (!this.dwell) return;
      var run =
        this.autoActive && this.inView && !this.userPaused && !this.hovering && !document.hidden;
      if (run) this.dwell.play();
      else this.dwell.pause();
    }

    /* Nach der letzten Station geht es zurück auf Station 1. */
    advance() {
      if (!this.autoActive) return;
      var next = this.index + 1;
      if (next > this.stations.length - 1) next = 0;
      this.goTo(next);
    }

    /* Sobald jemand selbst bedient, ist der Filmlauf für diesen Besuch
       vorbei – kein Pausieren, kein späteres Wiederanspringen. */
    stopAuto() {
      if (!this.autoActive) return;
      this.autoActive = false;
      if (this.dwell) {
        this.dwell.cancel();
        this.dwell = null;
      }
      this.classList.remove('is-auto');
      if (this.pauseButton) this.pauseButton.hidden = true;
    }

    /* Letzte Station: der Rundgang endet am echten Grundriss weiter unten
       auf der Seite. */
    finish() {
      var target = this.nextButton && this.nextButton.getAttribute('data-finish-target');
      var section = target ? document.querySelector(target) : null;
      if (!section) return;
      section.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    setupSwipe() {
      var stage = this.querySelector('[data-hl-tour-stage]');
      if (!stage) return;
      var self = this;
      var startX = null;
      var startY = null;

      stage.addEventListener(
        'touchstart',
        function (event) {
          startX = event.changedTouches[0].clientX;
          startY = event.changedTouches[0].clientY;
        },
        { passive: true }
      );

      stage.addEventListener(
        'touchend',
        function (event) {
          if (startX === null) return;
          var dx = event.changedTouches[0].clientX - startX;
          var dy = event.changedTouches[0].clientY - startY;
          startX = null;
          // Nur waagerechtes Wischen zählt.
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
          self.stopAuto();
          self.goTo(self.index + (dx < 0 ? 1 : -1));
        },
        { passive: true }
      );
    }

    goTo(index) {
      var last = this.stations.length - 1;
      if (index < 0 || index > last || index === this.index) return;
      // Während der Türbewegung werden weitere Klicks ignoriert.
      if (this.isMoving) return;

      var from = this.stations[this.index];
      var to = this.stations[index];
      var forward = index > this.index;

      this.index = index;
      this.isMoving = true;
      // Während des Übergangs ist die Fortschrittslinie ausgeblendet; die
      // Standzeit beginnt erst, wenn die Tür fertig ist.
      if (this.dwell) this.dwell.cancel();
      this.classList.add('is-moving');
      this.render();

      this.playDoor(from, to, forward);

      var panel = to.querySelector('[data-hl-station-panel]');
      if (panel) {
        panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
      }
    }

    /* Vorwärts blendet die Tür der bisherigen Station ein, öffnet sich, und
       die neue Station wächst aus dem Türausschnitt heraus (1,8 s).
       Rückwärts läuft dieselbe Bewegung umgekehrt. */
    playDoor(from, to, forward) {
      var self = this;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var duration = reduced ? 300 : 1800;

      // Der Ausschnitt muss dort sitzen, wo die Tür steht: vorwärts an der
      // Tür der weichenden Station, rückwärts an der der neuen.
      var doorSource = forward ? from : to;
      var clipTarget = forward ? to : from;
      this.copyDoorMetrics(doorSource, clipTarget);

      from.classList.add('is-leaving');
      from.classList.add(forward ? 'is-door-open' : 'is-camera-out');
      to.classList.add(forward ? 'is-camera-in' : 'is-door-close');

      if (!reduced) {
        from.style.willChange = 'transform, opacity, clip-path';
        to.style.willChange = 'transform, opacity, clip-path';
      }

      clearTimeout(this._doorTimer);
      this._doorTimer = setTimeout(function () {
        from.classList.remove('is-leaving', 'is-door-open', 'is-camera-out');
        to.classList.remove('is-camera-in', 'is-door-close');
        from.style.willChange = '';
        to.style.willChange = '';
        self.clearDoorMetrics();
        self.isMoving = false;
        self.classList.remove('is-moving');
        self.startDwell();
      }, duration);
    }

    /* Türmaße von einer Station auf eine andere übertragen: Lage aus den
       Einstellungen der Station, Höhe aus ihrem berechneten Wert. */
    copyDoorMetrics(source, target) {
      var door = source.querySelector('[data-door-x]');
      if (!door) return;
      target.style.setProperty('--hl-door-cx', door.getAttribute('data-door-x') + '%');
      target.style.setProperty('--hl-door-cy', door.getAttribute('data-door-y') + '%');

      var height = window.getComputedStyle(source).getPropertyValue('--hl-door-h');
      if (height) target.style.setProperty('--hl-door-h', height.trim());
      this.clipTarget = target;
    }

    /* Nach dem Übergang wieder abräumen, sonst schleppt eine Station die
       Maße der vorigen mit. */
    clearDoorMetrics() {
      var target = this.clipTarget;
      if (!target) return;
      target.style.removeProperty('--hl-door-cx');
      target.style.removeProperty('--hl-door-cy');
      target.style.removeProperty('--hl-door-h');
      this.clipTarget = null;
    }

    render() {
      var current = this.index;
      var last = this.stations.length - 1;

      this.stations.forEach(function (station, i) {
        var state = i === current ? 'current' : i < current ? 'past' : 'coming';
        station.setAttribute('data-state', state);
        station.setAttribute('aria-hidden', i === current ? 'false' : 'true');
        // Nicht sichtbare Stationen aus der Tastaturreihenfolge nehmen.
        Array.prototype.forEach.call(
          station.querySelectorAll('a, button, [data-hl-door-trigger]'),
          function (el) {
            if (i !== current) {
              el.setAttribute('tabindex', '-1');
            } else if (el.hasAttribute('data-hl-door-trigger')) {
              el.setAttribute('tabindex', '0');
            } else {
              el.removeAttribute('tabindex');
            }
          }
        );
      });

      this.steps.forEach(function (step, i) {
        step.setAttribute('aria-current', i === current ? 'true' : 'false');
      });

      // Auf der ersten Station gibt es kein Zurück – dann steht dort auch
      // kein leerer Kreis, die Bedienleiste rückt zusammen.
      if (this.prevButton) {
        this.prevButton.disabled = current === 0;
        this.prevButton.hidden = current === 0;
      }

      var finishLabel = this.nextButton ? this.nextButton.getAttribute('data-label-finish') : '';
      if (this.nextButton) this.nextButton.disabled = current === last && !finishLabel;

      if (this.nextLabel) {
        var enterLabel = this.nextButton.getAttribute('data-label-enter');
        var label;

        if (current === 0 && enterLabel) {
          // An der Fassade heißt der Knopf „Treten Sie ein" und ist golden.
          label = enterLabel;
          this.nextButton.classList.add('hl-btn--gold');
          this.nextButton.classList.remove('hl-btn--primary');
        } else if (current === last && finishLabel) {
          // Am Ende führt er zum Grundriss, nicht eine Station weiter.
          label = finishLabel;
          this.nextButton.classList.add('hl-btn--gold');
          this.nextButton.classList.remove('hl-btn--primary');
        } else {
          label = this.nextButton.getAttribute('data-label-next');
          var nextStation = this.stations[current + 1];
          if (nextStation) {
            var name = nextStation.getAttribute('data-station-name');
            if (name) label = label + ' · ' + name;
          }
          this.nextButton.classList.remove('hl-btn--gold');
          this.nextButton.classList.add('hl-btn--primary');
        }

        this.nextLabel.textContent = label;
      }

      if (this.liveRegion) {
        this.liveRegion.textContent = this.liveRegion
          .getAttribute('data-format')
          .replace('[current]', current + 1)
          .replace('[total]', this.stations.length);
      }
    }
  }

  if (!customElements.get('hl-house-tour')) customElements.define('hl-house-tour', HlHouseTour);

  /* ------------------------------------------------------------------
     Schnellansicht – lädt die Section „hl-quick-view" an der Produkt-URL
     ------------------------------------------------------------------ */
  HL.openQuickView = function (productUrl, trigger) {
    var drawer = document.querySelector('#hl-quick-view-drawer');
    if (!drawer || !productUrl) return;

    var body = drawer.querySelector('[data-hl-quick-body]');
    if (body) body.innerHTML = body.getAttribute('data-loading-html') || '';
    drawer.open(trigger);

    var separator = productUrl.indexOf('?') === -1 ? '?' : '&';
    fetch(productUrl + separator + 'section_id=hl-quick-view')
      .then(function (response) {
        if (!response.ok) throw new Error('quick view');
        return response.text();
      })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html');
        var content = fresh.querySelector('[data-hl-quick-content]');
        if (content && body) body.innerHTML = content.innerHTML;
      })
      .catch(function () {
        // Wenn die Schnellansicht nicht lädt, zur Produktseite wechseln.
        window.location.href = productUrl;
      });
  };

  /* Warenkorb-Knopf in der Schnellansicht */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-hl-quick-form]');
    if (!form) return;
    event.preventDefault();

    var button = form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-busy', 'true');

    var body = new FormData(form);
    body.append('sections', 'hl-cart-drawer,hl-header');
    body.append('sections_url', window.location.pathname);

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: body,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) return;
        HL.applyCartSections(data.sections);
        HL.toast();
        var quick = document.querySelector('#hl-quick-view-drawer');
        if (quick && typeof quick.close === 'function') quick.close();
        var cart = document.querySelector('#hl-cart-drawer');
        if (cart && typeof cart.open === 'function') cart.open(button);
      })
      .finally(function () {
        if (button) button.removeAttribute('aria-busy');
      });
  });

  /* Variantenwahl in der Schnellansicht: ausgewählte Variante übernehmen */
  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-hl-quick-variant]')) return;
    var form = event.target.closest('[data-hl-quick-form]');
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = event.target.value;

    var option = event.target.selectedOptions ? event.target.selectedOptions[0] : null;
    var price = form.querySelector('[data-hl-quick-price]');
    if (option && price && option.dataset.price) price.textContent = option.dataset.price;
    var button = form.querySelector('[type="submit"]');
    if (option && button) button.disabled = option.dataset.available !== 'true';
  });
})();

/* ==========================================================================
   D9 · Pillen am Grundriss spiegeln

   Sitzt ein Punkt in der rechten Bildschirmhaelfte, oeffnet seine Pille nach
   links. Gemessen wird die tatsaechliche Lage im Fenster, nicht die im
   Prozentwert hinterlegte - so stimmt es auch, wenn das Bild beschnitten ist.
   ========================================================================== */
(function () {
  'use strict';

  function flipPills() {
    var spots = document.querySelectorAll('.hl-tour__hotspot');
    if (!spots.length) return;
    var width = window.innerWidth || document.documentElement.clientWidth;

    Array.prototype.forEach.call(spots, function (spot) {
      var dot = spot.querySelector('.hl-tour__dot') || spot;
      var mid = dot.getBoundingClientRect().left + dot.getBoundingClientRect().width / 2;
      if (!mid) return;
      spot.setAttribute('data-hl-flip', mid > width * 0.55 ? 'true' : 'false');
    });
  }

  var pending = null;
  function schedule() {
    if (pending) cancelAnimationFrame(pending);
    pending = requestAnimationFrame(function () {
      pending = null;
      flipPills();
    });
  }

  // Der erste Durchgang laeuft sofort, nicht erst im naechsten Bild: Liegt
  // die Seite im Hintergrund, kommt kein `requestAnimationFrame` - und die
  // Pillen stuenden bis zum ersten Scrollen falsch herum.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flipPills);
  } else {
    flipPills();
  }
  // Noch einmal, sobald Schrift und Bilder stehen - die Punkte sitzen dann
  // an ihrer endgueltigen Stelle.
  window.addEventListener('load', flipPills, { once: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', flipPills);
})();
